import React, { Dispatch, SetStateAction } from 'react';
import { AppointmentDateRangeType, NumberOfPetParentsReached } from 'shared/types/graphql';
import { SelectedDateRange } from '..';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { formatPluralization } from 'shared/utils';
import { nextMonday, nextSunday } from 'date-fns';
import { useTracking } from 'shared/providers/TrackingProvider';
import { Flex, HStack, VStack } from '@televet/kibble-ui/build/chakra';
import { Radio } from '@televet/kibble-ui/build/components/Radio';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { MassTextAlertsModalName } from '../records/MassTextAlertsModalName';
import { MassTextAlertEvents } from 'shared/providers/TrackingProvider/EventNames';

interface DateRangeSelectorProps {
  sendTimeData?: Pick<NumberOfPetParentsReached, 'numberOfPetPerents' | 'timeEstimateInSeconds'> | null;
  selectedDateRange: SelectedDateRange | null;
  formatDateRange: (dateRange: { startDate?: Date; endDate?: Date }) => string;
  setSelectedDateRange: Dispatch<SetStateAction<SelectedDateRange | null>>;
  fetchEstimatedSendTime: (startDate?: Date | undefined, endDate?: Date | undefined) => void;
  openModal: (modalName: MassTextAlertsModalName) => void;
}

const MassTextAlertsDateRangeSelector = ({
  selectedDateRange,
  openModal,
  setSelectedDateRange,
  sendTimeData,
  formatDateRange,
  fetchEstimatedSendTime,
}: DateRangeSelectorProps): JSX.Element => {
  const { trackEvent } = useTracking();
  const dateRangeOptions = [
    {
      title: 'Through Today',
      value: AppointmentDateRangeType.Today,
    },
    {
      title: 'Tomorrow',
      value: AppointmentDateRangeType.Tomorrow,
    },
    {
      title: 'Through This Week',
      value: AppointmentDateRangeType.ThisWeek,
    },
    {
      title: 'Next Week',
      value: AppointmentDateRangeType.NextWeek,
    },
    {
      title: 'Custom Date Range',
      value: AppointmentDateRangeType.Custom,
    },
  ];

  const handleDateRangeSelection = (selectedOption: AppointmentDateRangeType): void => {
    let startDate = new Date();
    let endDate = new Date();

    switch (selectedOption) {
      case AppointmentDateRangeType.Today:
        endDate.setDate(startDate.getDate());
        break;
      case AppointmentDateRangeType.Tomorrow:
        startDate.setHours(0, 0, 0, 0);
        startDate.setDate(startDate.getDate() + 1);
        endDate.setDate(startDate.getDate());
        endDate.setMonth(startDate.getMonth());
        break;
      case AppointmentDateRangeType.ThisWeek:
        endDate = nextSunday(startDate);
        break;
      case AppointmentDateRangeType.NextWeek:
        startDate.setHours(0, 0, 0, 0);
        startDate = nextMonday(startDate);
        endDate = nextSunday(startDate);
        break;
      case AppointmentDateRangeType.Custom:
        openModal(MassTextAlertsModalName.SelectMassTextAlertCustomDateRange);
        break;
      default:
        break;
    }

    endDate.setHours(23, 59, 59, 999);

    const data: SelectedDateRange = {
      optionName: selectedOption,
      startDate,
      endDate,
    };

    if (selectedOption !== AppointmentDateRangeType.Custom) {
      setSelectedDateRange(data);
      trackEvent(MassTextAlertEvents.EditSelectedDateRange, {
        optionName: selectedOption,
        startDate: startDate.toDateString(),
        endDate: endDate.toDateString(),
      });

      try {
        fetchEstimatedSendTime(data.startDate, data.endDate);
      } catch (e) {
        console.log(e);
      }
    }
  };

  return (
    <>
      <Text as="p" fontWeight="bold" size="sm" mb={2}>
        Send to All Appointments in Date Range:
      </Text>
      <Flex
        flexWrap="wrap"
        gap={2}
        className="MassAlert__DateRange-ButtonContainer"
        align="flex-start"
        justify="flex-start"
      >
        {dateRangeOptions.map((option) => {
          const isActive = option.value === selectedDateRange?.optionName;
          return (
            <HStack
              className="MassAlert__DateRange-Button"
              key={option.title + option.value}
              onClick={(): void => handleDateRangeSelection(option.value)}
              borderRadius="md"
              justify="flex-start"
              cursor="pointer"
              border="1px"
              pr={2}
              borderColor={isActive ? 'border.info' : 'border.default'}
              bg="background.alpha.hover"
              _hover={{
                borderColor: 'border.info',
              }}
            >
              <Radio w="0" h="0" p="0" name="radio" visibility="hidden" value={option.value} />
              <Text size="xs" fontWeight={isActive ? 'bold' : 'regular'}>
                {option.title}
              </Text>
              {option.value === AppointmentDateRangeType.Custom && <Icon name="chevronDown" size="xs" />}
            </HStack>
          );
        })}
      </Flex>

      <VStack
        visibility={Boolean(selectedDateRange?.optionName && sendTimeData) ? 'visible' : 'hidden'}
        align="flex-start"
        spacing={0}
        mt={2}
      >
        <Text as="p" size="xs" fontStyle="italic">
          {sendTimeData?.numberOfPetPerents === 0
            ? `No clients with appointments `
            : `Sending to ${sendTimeData?.numberOfPetPerents} ${formatPluralization({
                word: 'client',
                singularCriteria: sendTimeData?.numberOfPetPerents || 0,
              })} with ${formatPluralization({
                word: 'appointment',
                needsArticle: true,
                singularCriteria: sendTimeData?.numberOfPetPerents || 0,
              })} `}
        </Text>
        <Text as="p" size="xs" fontStyle="italic">
          {selectedDateRange &&
            formatDateRange({ startDate: selectedDateRange.startDate, endDate: selectedDateRange.endDate })}
        </Text>
      </VStack>
    </>
  );
};

export default MassTextAlertsDateRangeSelector;
